import { useAuthStore } from '@/stores/auth-store';
import type { CompanyInfo } from '@scio3/shared-types';

/**
 * Pure feature flag extraction — testable without React.
 * Returns boolean feature flags with safe defaults.
 *
 * `isSuperUser` (OptiSigns support staff) bypasses the plan gate for the
 * device-management features so support can assist customers on any plan —
 * mirrors scio1 principal.getAdvancedDeviceManagement() / getRemoteControl().
 */
export function getCompanyFeatures(company: CompanyInfo | null, isSuperUser = false) {
  const feature = company?.feature;
  const featureData = company?.featureData;

  return {
    isRemoteCommand: featureData?.isRemoteCommand ?? false,
    allowTeams: feature?.allowTeams ?? false,
    samlSSO: feature?.samlSSO ?? false,
    proofOfPlay: feature?.proofOfPlay ?? false,
    proofOfPlayStatus: featureData?.proofOfPlayStatus ?? false,
    ai: feature?.ai ?? false,
    engage: feature?.engage ?? false,
    apiKey: feature?.apiKey ?? false,
    apiAccessToken: feature?.apiAccessToken ?? false,
    branding: feature?.branding ?? false,
    brandingCustomDomain: feature?.brandingCustomDomain ?? false,
    approvalWorkflow: feature?.approvalWorkflow ?? false,
    contentApproval: feature?.contentApproval ?? false,
    customBrandCanva: feature?.customBrandCanva ?? false,
    folderLevelSecurity: feature?.folderLevelSecurity ?? false,
    // Support accounts bypass the plan gate (see getCompanyFeatures jsdoc)
    advancedDeviceManagement: (feature?.advancedDeviceManagement ?? false) || isSuperUser,
    advancedSchedule: feature?.advancedSchedule ?? false,
    cecSchedule: feature?.cecSchedule ?? false,
    remoteControl: (feature?.remoteControl ?? false) || isSuperUser,
    remoteCommand: feature?.remoteCommand ?? false,
    // SecureLink PLAN capability — used per-device (Edit Screen toggle): OptiSigns-sold hardware is
    // EXEMPT, non-OptiSigns/BYOD devices require this plan flag (L1 posture §5.1).
    secureLink: feature?.secureLink ?? false,
    // Account-level gate for the device-troubleshoot bot (OptiBot button): only the staff kill switch
    // (disableSecureLink) — permissive by plan. The precise per-device plan gate (OptiSigns exempt, BYOD
    // requires plan) is enforced by the backend hard gate + the Edit Screen toggle. `!!company` keeps it
    // off until the company loads; per-device reachability (securelinkIp) is checked at the call site.
    secureLinkTroubleshootEnabled: !!company && feature?.disableSecureLink !== true,
    lockdownDevice: feature?.lockdownDevice ?? false,
    sensor: feature?.sensor ?? false,
    roleSecurity: feature?.roleSecurity ?? false,
    securityAuditLogging: feature?.securityAuditLogging ?? false,
    alertPolicy: feature?.alertPolicy ?? false,
    externalCom: feature?.externalCom ?? false,
    comLog: feature?.comLog ?? false,
    shareLink: feature?.shareLink ?? false,
    totpMfa: feature?.totpMfa ?? false,
    phoneMfa: feature?.phoneMfa ?? false,
    contentPlaylistTag: feature?.contentPlaylistTag ?? false,
    subPlaylistControl: feature?.subPlaylistControl ?? false,
    playEvery: feature?.playEvery ?? false,
    resumeOnNextPlay: feature?.resumeOnNextPlay ?? false,
    feedsCreate: feature?.feedsCreate ?? false,
    feedsManage: feature?.feedsManage ?? false,
    feedsCategories: feature?.feedsCategories ?? false,
    enterpriseAPP: feature?.enterpriseAPP ?? false,
    // Enterprise / billing-managed accounts can opt out of the unpaid-invoice
    // gate so the wizard / payment flows don't block them. SCIO 1.0 parity:
    // subscriptionPlanModal.js:1956.
    disablePaymentCheck: feature?.disablePaymentCheck ?? false,
    enforcePasswordComplexity: feature?.enforcePasswordComplexity ?? false,
    utilsMDM: feature?.utilsMDM ?? false,
    // Mass provisioning (Provisioning Templates + Wi-Fi/802.1X authoring).
    // Enabled per-customer-request (not plan-granted); backend resolvers gate
    // create/edit/push with @RequireFeature('massProvision').
    massProvision: feature?.massProvision ?? false,
    aericast: feature?.aericast ?? false,
    apiGateway: feature?.apiGateway ?? false,
    dynamicDataMapping: feature?.dynamicDataMapping ?? false,
    isSecurityAuditLogging: featureData?.isSecurityAuditLogging ?? false,
    // Infrastructure / Data Residency
    disableS3: feature?.disableS3 ?? false,
    disableDOSpaces: feature?.disableDOSpaces ?? false,
    powerbiServicePrincipal: feature?.powerbiServicePrincipal ?? false,
    allowPowerBI: feature?.allowPowerBI ?? false,
    allowLooker: feature?.allowLooker ?? false,
    teamBilling: feature?.teamBilling ?? false,
    scim: feature?.scim ?? false,
    smallappManifest: feature?.smallappManifest ?? false,
    // Order-device addons: gates "OptiSigns Managed Device" (requiredPlan-gated addon)
    // from the device selector when false (SCIO 1.0 orderDeviceComponent.html:148).
    orderManagedDevice: feature?.orderManagedDevice ?? false,
    // International ordering enabled: when true, suppress the non-US shipping
    // warning dialog on Order Device. SCIO 1.0 orderDeviceComponent.js:1112,1130
    // gates `showWarningOutsideUSModal` on `!myCompany.feature.allowIntlOrder`.
    allowIntlOrder: feature?.allowIntlOrder ?? false,
    // Per-company virtual screen cap. Defaults to 1 when not set.
    virtualScreenCountLimit: feature?.virtualScreenCountLimit ?? 1,
  };
}

/**
 * React hook wrapper for getCompanyFeatures.
 * Reads company from auth store and delegates to pure function.
 * `loaded` is false while company data is still being fetched — use it
 * to avoid flashing plan-gate UIs before the feature flags are known.
 */
export function useCompanyFeatures() {
  const company = useAuthStore((s) => s.company);
  const companyLoaded = useAuthStore((s) => s.companyLoaded);
  const isSuperUser = useAuthStore((s) => s.me?.isSuperUser ?? false);
  return { ...getCompanyFeatures(company, isSuperUser), loaded: companyLoaded };
}
